import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Users, Plus, MagnifyingGlass, Phone as PhoneIcon, WhatsappLogo, NotePencil, Star, Clock, PencilSimple, Trash, Check, CalendarBlank, Funnel, ArrowRight, MapPin } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth, isAdmin } from "@/lib/access";

const API = `${window.location.origin}/api`;

function daysSince(dateStr) {
  if (!dateStr) return 999;
  const d = new Date(dateStr + "T00:00:00");
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function getActivityStatus(lastContact) {
  const days = daysSince(lastContact);
  if (days <= 7) return { label: "Active", color: "bg-green-100 text-green-700 border-green-200" };
  if (days <= 30) return { label: `${days}d ago`, color: "bg-amber-100 text-amber-700 border-amber-200" };
  return { label: "Inactive", color: "bg-red-100 text-red-700 border-red-200" };
}

/* ====== Customer Card ====== */
function CustomerCard({ customer, onCall, onNote, onEdit, onDelete, onToggleVip }) {
  const activity = getActivityStatus(customer.last_contact_date);
  const needsCall = customer.next_call_date && customer.next_call_date <= new Date().toISOString().split("T")[0];

  return (
    <Card className={`p-3 border bg-white ${needsCall ? "border-orange-300 bg-orange-50/30" : "border-gray-200"}`} data-testid={`customer-${customer.customer_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${customer.is_vip ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-600"}`}>
            {customer.is_vip && <Star size={12} weight="fill" className="absolute -mt-6 -mr-6 text-amber-500" />}
            {customer.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="font-bold text-sm text-gray-900 truncate">{customer.name}</p>
              {customer.is_vip && <Star size={12} weight="fill" className="text-amber-500 flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
              {customer.phone && <span className="text-[10px] text-gray-500 flex items-center gap-0.5"><PhoneIcon size={10} />{customer.phone}</span>}
              {customer.company && <span className="text-[10px] text-gray-400">{customer.company}</span>}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold border ${activity.color}`}>{activity.label}</span>
            </div>
            {customer.product_interested && <p className="text-[10px] text-indigo-500 mt-0.5 truncate">{customer.product_interested}</p>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {customer.total_orders > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold">{customer.total_orders} orders</span>}
          {needsCall && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold border border-orange-200">Call Today</span>}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <div className="flex gap-1.5">
          {customer.phone && (
            <a href={`tel:${customer.phone}`} onClick={() => onCall(customer)} className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg text-[11px] font-bold text-green-700 touch-target" data-testid={`call-${customer.customer_id}`}>
              <PhoneIcon size={12} weight="bold" /> Call
            </a>
          )}
          {customer.phone && (
            <a href={`https://wa.me/${customer.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-[11px] font-bold text-emerald-700 touch-target" data-testid={`whatsapp-${customer.customer_id}`}>
              <WhatsappLogo size={12} weight="bold" /> WhatsApp
            </a>
          )}
          <button onClick={() => onNote(customer)} className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-[11px] font-bold text-indigo-700 touch-target" data-testid={`note-${customer.customer_id}`}>
            <NotePencil size={12} weight="bold" /> Note
          </button>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onToggleVip(customer)} className={`p-1.5 rounded-lg touch-target ${customer.is_vip ? "bg-amber-100 text-amber-600" : "hover:bg-gray-100 text-gray-400"}`} title={customer.is_vip ? "Remove VIP" : "Mark VIP"} data-testid={`vip-${customer.customer_id}`}>
            <Star size={14} weight={customer.is_vip ? "fill" : "regular"} />
          </button>
          <button onClick={() => onEdit(customer)} className="p-1.5 rounded-lg hover:bg-gray-100 text-indigo-600 touch-target" data-testid={`edit-cust-${customer.customer_id}`}><PencilSimple size={14} /></button>
          <button onClick={() => onDelete(customer)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 touch-target" data-testid={`delete-cust-${customer.customer_id}`}><Trash size={14} /></button>
        </div>
      </div>

      {/* Notes preview */}
      {customer.notes && <p className="text-[10px] text-gray-400 mt-1 truncate">{customer.notes}</p>}
    </Card>
  );
}

/* ====== Main Page ====== */
export default function CustomersPage() {
  const user = useAuth();
  const admin = isAdmin(user);
  const [customers, setCustomers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterMember, setFilterMember] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [noteCustomer, setNoteCustomer] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteNextCall, setNoteNextCall] = useState("");
  const [editCustomer, setEditCustomer] = useState(null);
  const [showContacts, setShowContacts] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactCustomer, setContactCustomer] = useState(null);

  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", location: "", product_interested: "", notes: "", next_call_date: "", assigned_to: "" });
  const [editForm, setEditForm] = useState({ name: "", company: "", phone: "", email: "", location: "", product_interested: "", notes: "", next_call_date: "", total_orders: 0, assigned_to: "" });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterType && filterType !== "all") params.filter_type = filterType;
      if (filterMember && filterMember !== "all") params.assigned_to = filterMember;
      if (filterLocation && filterLocation !== "all") params.location = filterLocation;
      const [custRes, dashRes, teamRes] = await Promise.all([
        axios.get(`${API}/customers`, { params }),
        axios.get(`${API}/customers/dashboard`),
        axios.get(`${API}/team`),
      ]);
      setCustomers(custRes.data);
      setDashboard(dashRes.data);
      setTeamMembers(admin ? teamRes.data.filter(m => m.status === "Active") : [{ id: "me", name: user.assignee, status: "Active" }]);
      // Extract unique locations
      const locs = [...new Set(custRes.data.map(c => c.location).filter(Boolean))].sort();
      setLocations(locs);
    } catch (err) { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  }, [search, filterType, filterMember, filterLocation, admin, user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error("Enter customer name"); return; }
    try {
      await axios.post(`${API}/customers`, form);
      toast.success(`${form.name} added!`);
      setShowAddDialog(false);
      setForm({ name: "", company: "", phone: "", email: "", location: "", product_interested: "", notes: "", next_call_date: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (c) => {
    setEditCustomer(c);
    setEditForm({ name: c.name, company: c.company, phone: c.phone, email: c.email, location: c.location, product_interested: c.product_interested, notes: c.notes, next_call_date: c.next_call_date || "", total_orders: c.total_orders || 0, assigned_to: c.assigned_to || "" });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/customers/${editCustomer.customer_id}`, editForm);
      toast.success("Updated!");
      setShowEditDialog(false);
      fetchData();
    } catch (err) { toast.error("Update failed"); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}?`)) return;
    try { await axios.delete(`${API}/customers/${c.customer_id}`); toast.success("Deleted"); fetchData(); } catch (err) { toast.error("Failed"); }
  };

  const handleCall = async (c) => {
    try { await axios.post(`${API}/customers/${c.customer_id}/log-contact`, { type: "call", notes: "Phone call" }); fetchData(); } catch (err) { /* silent */ }
  };

  const openNote = (c) => { setNoteCustomer(c); setNoteText(""); setNoteNextCall(""); setShowNoteDialog(true); };

  const handleSaveNote = async () => {
    if (!noteCustomer) return;
    try {
      await axios.post(`${API}/customers/${noteCustomer.customer_id}/log-contact`, { type: "note", notes: noteText || "Note added", next_call_date: noteNextCall });
      toast.success("Note saved!");
      setShowNoteDialog(false);
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  const handleToggleVip = async (c) => {
    try {
      await axios.put(`${API}/customers/${c.customer_id}`, { is_vip: !c.is_vip });
      toast.success(c.is_vip ? "VIP removed" : "Marked as VIP!");
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  const openContacts = async (c) => {
    setContactCustomer(c);
    try {
      const res = await axios.get(`${API}/customers/${c.customer_id}/contacts`);
      setContacts(res.data);
      setShowContacts(true);
    } catch (err) { toast.error("Failed to load"); }
  };

  const d = dashboard || { total: 0, to_call_today: 0, repeat_this_month: 0, vip: 0, inactive: 0 };

  const statCards = [
    { label: "Total Customers", value: d.total, icon: Users, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200", filter: "all" },
    { label: "Call Today", value: d.to_call_today, icon: PhoneIcon, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200", filter: "to_call" },
    { label: "Repeat This Month", value: d.repeat_this_month, icon: ArrowRight, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", filter: "" },
    { label: "VIP Customers", value: d.vip, icon: Star, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", filter: "vip" },
    { label: "Inactive", value: d.inactive, icon: Clock, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", filter: "inactive" },
  ];

  return (
    <div className="space-y-4 max-w-7xl" data-testid="customers-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Regular Customers</h1>
          <p className="text-xs text-gray-500">{customers.length} customers</p>
        </div>
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-9" onClick={() => { setForm({ name: "", company: "", phone: "", email: "", location: "", product_interested: "", notes: "", next_call_date: "", assigned_to: "" }); setShowAddDialog(true); }} data-testid="add-customer-btn">
          <Plus size={14} className="mr-1" /> Add Customer
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="customer-stats">
        {statCards.map((c) => (
          <Card key={c.label} className={`p-3 border ${c.border} bg-white ${c.filter ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`} onClick={() => c.filter && setFilterType(filterType === c.filter ? "" : c.filter)} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0`}><c.icon size={16} className={c.color} weight="bold" /></div>
              <div><p className="text-lg font-bold text-gray-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.value}</p><p className="text-[10px] text-gray-500">{c.label}</p></div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center overflow-x-auto mobile-scroll-x">
        <div className="relative flex-1 min-w-[150px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search customer..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-sm" data-testid="customer-search" />
        </div>
        <Select value={filterMember} onValueChange={setFilterMember}>
          <SelectTrigger className="w-[130px] h-9 text-xs flex-shrink-0" data-testid="filter-member"><SelectValue placeholder="Team Member" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Members</SelectItem>
            {teamMembers.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterLocation} onValueChange={setFilterLocation}>
          <SelectTrigger className="w-[120px] h-9 text-xs flex-shrink-0" data-testid="filter-location"><SelectValue placeholder="Location" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filterType || filterMember || filterLocation) && (
          <Button variant="ghost" size="sm" className="h-9 text-xs text-red-500 flex-shrink-0" onClick={() => { setFilterType(""); setFilterMember(""); setFilterLocation(""); }} data-testid="clear-filter">Clear</Button>
        )}
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={`skel-${i}`} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : customers.length === 0 ? (
        <Card className="p-8 text-center border border-gray-200 bg-white" data-testid="empty-customers">
          <Users size={40} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400 font-semibold">No customers yet</p>
          <p className="text-xs text-gray-400 mt-1">When a lead is marked as "Won", they become a regular customer automatically. Or add one manually.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="customer-list">
          {customers.map((c) => (
            <CustomerCard key={c.customer_id} customer={c} onCall={handleCall} onNote={openNote} onEdit={handleEdit} onDelete={handleDelete} onToggleVip={handleToggleVip} />
          ))}
        </div>
      )}

      {/* ====== Add Customer Dialog ====== */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Gupter', serif" }}>Add Regular Customer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-sm font-semibold">Customer Name *</Label><Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="h-11" data-testid="add-name" autoFocus /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Phone</Label><Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91 98765 43210" className="h-10" data-testid="add-phone" /></div>
              <div><Label className="text-sm font-semibold">Company</Label><Input value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Company name" className="h-10" data-testid="add-company" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" className="h-10" data-testid="add-email" /></div>
              <div><Label className="text-sm font-semibold">Location</Label><Input value={form.location} onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))} placeholder="City" className="h-10" data-testid="add-location" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Product Interest</Label><Input value={form.product_interested} onChange={(e) => setForm(p => ({ ...p, product_interested: e.target.value }))} placeholder="Product" className="h-10" data-testid="add-product" /></div>
              <div><Label className="text-sm font-semibold">Next Call Date</Label><Input type="date" value={form.next_call_date} onChange={(e) => setForm(p => ({ ...p, next_call_date: e.target.value }))} className="h-10" data-testid="add-next-call" /></div>
            </div>
            <div>
              <Label className="text-sm font-semibold">Assigned To</Label>
              <Select value={form.assigned_to} onValueChange={(v) => setForm(p => ({ ...p, assigned_to: v }))}>
                <SelectTrigger className="h-10" data-testid="add-assigned"><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-semibold">Notes</Label><Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any notes..." className="h-10" data-testid="add-notes" /></div>
            <Button onClick={handleAdd} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="save-customer-btn"><Plus size={16} className="mr-1" /> Add Customer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Edit Dialog ====== */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle className="text-lg font-bold">Edit Customer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-sm font-semibold">Name</Label><Input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} className="h-10" data-testid="edit-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} className="h-10" data-testid="edit-phone" /></div>
              <div><Label className="text-sm font-semibold">Company</Label><Input value={editForm.company} onChange={(e) => setEditForm(p => ({ ...p, company: e.target.value }))} className="h-10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Email</Label><Input value={editForm.email} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} className="h-10" /></div>
              <div><Label className="text-sm font-semibold">Location</Label><Input value={editForm.location} onChange={(e) => setEditForm(p => ({ ...p, location: e.target.value }))} className="h-10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Product</Label><Input value={editForm.product_interested} onChange={(e) => setEditForm(p => ({ ...p, product_interested: e.target.value }))} className="h-10" /></div>
              <div><Label className="text-sm font-semibold">Total Orders</Label><Input type="number" min="0" value={editForm.total_orders} onChange={(e) => setEditForm(p => ({ ...p, total_orders: parseInt(e.target.value) || 0 }))} className="h-10" data-testid="edit-orders" /></div>
            </div>
            <div><Label className="text-sm font-semibold">Next Call</Label><Input type="date" value={editForm.next_call_date} onChange={(e) => setEditForm(p => ({ ...p, next_call_date: e.target.value }))} className="h-10" /></div>
            <div>
              <Label className="text-sm font-semibold">Assigned To</Label>
              <Select value={editForm.assigned_to} onValueChange={(v) => setEditForm(p => ({ ...p, assigned_to: v }))}>
                <SelectTrigger className="h-10" data-testid="edit-assigned"><SelectValue placeholder="Team member" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-semibold">Notes</Label><Input value={editForm.notes} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} className="h-10" /></div>
            <Button onClick={handleSaveEdit} className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="save-edit-btn"><Check size={16} className="mr-1" /> Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Note / Contact Log Dialog ====== */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="text-lg font-bold">Add Note — {noteCustomer?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-sm font-semibold">Note</Label><Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="What happened? Any updates..." className="h-10" data-testid="note-text" autoFocus /></div>
            <div><Label className="text-sm font-semibold">Next Call Date</Label><Input type="date" value={noteNextCall} onChange={(e) => setNoteNextCall(e.target.value)} className="h-10" data-testid="note-next-call" /></div>
            <div className="flex gap-2">
              <Button onClick={handleSaveNote} className="flex-1 h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="save-note-btn"><NotePencil size={16} className="mr-1" /> Save Note</Button>
              <Button variant="outline" onClick={() => openContacts(noteCustomer)} className="h-10 text-xs" data-testid="view-history-btn">View History</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Contact History Dialog ====== */}
      <Dialog open={showContacts} onOpenChange={setShowContacts}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold">{contactCustomer?.name} — Contact History</DialogTitle></DialogHeader>
          <div className="py-2">
            {contacts.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No contact history yet</p>
            ) : (
              <div className="space-y-1.5">
                {contacts.map((c, i) => (
                  <div key={`contact-${i}`} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${c.type === "call" ? "bg-green-500" : "bg-indigo-500"}`} />
                      <span className="font-mono text-gray-500">{c.date}</span>
                      <span className="font-bold text-gray-700 capitalize">{c.type}</span>
                    </div>
                    <span className="text-gray-500 truncate ml-2 max-w-[200px]">{c.notes}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
